import React, { useEffect, useState } from "react";
import * as riskService from "../../../models/services/riskService";
import { ISessionState } from "../../../models/session/sessionState";
import { ListDetailsPanel } from "source/components/framework/List/ListDetailsPanel";
import { AppUtils } from "source/framework/appUtils";
import { IIdTitleNode, IdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { Translations } from "ui/Scripts/source/models/translations";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { Alert, Box, Grid, useTheme } from "@mui/material";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { ModifyUserInfo } from "source/components/framework/User/ModifyUserInfo";
import { ListDetailsPanelAccordion } from "source/components/framework/List/ListDetailsPanel/ListDetailsPanelAccordion";
import { EnumHelper, RiskType } from "ui/Scripts/source/models/enums";
import { FormFieldLockedToUser } from "source/components/framework/FormFieldLockedToUser";
import { IRiskEditItem } from "ui/Scripts/source/models/risk/riskEditItem";
import { Base } from "ui/Scripts/source/framework/base";
import { RiskListStateChip } from "./RiskListStateChip";
import { RiskEditDialogAssessmentList } from "../RiskEditDialogAssessmentList";
import { AttachmentListOfRecord } from "source/components/attachments/AttachmentList/AttachmentListOfRecord";

interface IRiskListDetailsPanelProps {
    sessionState: ISessionState;
    companyId: string;
    riskId: string;
    dataId: string;
    editDialogVisible: boolean;
    onRefreshList: () => void;
    onClose: () => void;
}

interface IRiskListDetailsPanelState {
    canEdit: boolean;
    risk: IRiskEditItem;
    riskCategory: IIdTitleNode;
    riskProcesses: IIdTitleNode[];
    riskStandards: IIdTitleNode[];
    riskStakeholders: IIdTitleNode[];
    riskProcessName: string;
    unit: IIdTitleNode;
    isLockedToOtherUser: boolean;
}

export const RiskListDetailsPanel = (props: IRiskListDetailsPanelProps) => {
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingAssessments, setIsLoadingAssessments] = useState(false);
    const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
    const [state, setState] = useState<IRiskListDetailsPanelState>({
        canEdit: false,
        risk: null,
        riskCategory: null,
        riskProcesses: [],
        riskStandards: [],
        riskStakeholders: [],
        riskProcessName: "",
        unit: null,
        isLockedToOtherUser: false,
    });

    const changeState = (obj: any) => {
        setState(prevState => ({
            ...prevState,
            ...obj
        }));
    };

    const loadRisk = async () => {
        if (!props.riskId) return;
        setIsLoading(true);
        const result = await AppUtils.callService(() => riskService.getRiskEdit(props.companyId, props.riskId, null, false), false, true, () => setIsLoading(false));
        if (!result) return;
        const risk = result.item;
        const riskCategory = IdTitleNode.getById(result.riskCategories, risk.riskCategoryId);
        changeState({
            canEdit: result.canEdit,
            risk,
            riskCategory,
            riskProcesses: IdTitleNode.getAsList(result.riskProcesses).filter(p => result.riskRiskProcessIds.includes(p.id)),
            riskStandards: IdTitleNode.getAsList(result.riskStandards).filter(s => result.riskRiskStandardIds.includes(s.id)),
            riskStakeholders: IdTitleNode.getAsList(result.riskStakeholders).filter(s => result.riskRiskStakeholderIds.includes(s.id)),
            riskProcessName: result.riskProcessName,
            unit: IdTitleNode.getById(result.units, risk.unitId),
            isLockedToOtherUser: risk.isLockedToOtherUser(props.sessionState.code),
        });
    };

    useEffect(() => {
        if (props.editDialogVisible) return;
        loadRisk();
    }, [props.companyId, props.riskId, props.dataId, props.editDialogVisible]);

    const isRisk = state.risk?.riskType === RiskType.Risk;

    const isPublished = !!state.risk?.publishUser;
    const isArchived = !!state.risk?.archiveUser;
    const isInTrash = !!state.risk?.deleteUser;
    const isReadOnly = !(state.canEdit && !isArchived);

    return (
        <ListDetailsPanel
            isLoading={isLoading}
            onClose={props.onClose}
        >
            {(!props.riskId || (!state.risk && isLoading)) &&
                <Alert severity="info">{Translations.SelectRiskInListToSeeThePreview}</Alert>
            }
            {!!state.risk &&
                <>
                    <div style={{ padding: "8px 16px 16px" }}>
                        <GridRow>
                            <Grid item sm={6}>
                                <FormField title={Translations.RiskCreated} fullWidth margin={0}>
                                    <ModifyUserInfo
                                        modifyUser={state.risk.createUser}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item sm={6}>
                                <FormField title={Translations.Modified} fullWidth margin={0}>
                                    <ModifyUserInfo
                                        modifyUser={state.risk.modifyUser}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        {state.isLockedToOtherUser &&
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormFieldLockedToUser
                                        lockedUser={state.risk.lockedUser}
                                    />
                                </Grid>
                            </GridRow>
                        }
                        {state.risk.archiveUser &&
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.Archived} fullWidth>
                                        <ModifyUserInfo
                                            modifyUser={state.risk.archiveUser}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                        {state.risk.deleteUser &&
                            <GridRow>
                                <Grid item sm={6}>
                                    <FormField title={Translations.MovedToTrashBin} fullWidth>
                                        <ModifyUserInfo
                                            modifyUser={state.risk.deleteUser}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                    </div>
                    <ListDetailsPanelAccordion
                        title={isRisk ? Translations.Risk : Translations.Opportunity}
                    >
                        <>
                            <GridRow>
                                <Grid item sm={3}>
                                    <FormField title={Translations.Code} margin={0}>
                                        {state.risk.code}
                                    </FormField>
                                </Grid>
                                <Grid item sm={3}>
                                    <FormField title={Translations.Unit} margin={0}>
                                        {state.unit.title}
                                    </FormField>
                                </Grid>
                                <Grid item sm={3}>
                                    <FormField title={Translations.Date} margin={0}>
                                        {Base.utcTimeToDateStr(state.risk.date)}
                                    </FormField>
                                </Grid>
                                <Grid item sm={3}>
                                    <FormField title={Translations.State} margin={0}>
                                        <RiskListStateChip
                                            code={isInTrash
                                                ? Translations.InTrashBin
                                                : (isArchived
                                                    ? Translations.Archived
                                                    : (isPublished
                                                        ? Translations.Published
                                                        : Translations.Draft))}
                                            color={isInTrash
                                                ? theme.palette.stateLightGray.main //InTrashBin
                                                : (isArchived
                                                    ? theme.palette.stateGray.main //Archived
                                                    : (isPublished
                                                        ? theme.palette.stateGreen.main //Published
                                                        : theme.palette.stateGray.main //Draft
                                                    )
                                                )
                                            }
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={3}>
                                    <FormField title={Translations.AType}>
                                        {state.risk.riskType === RiskType.Opportunity ? Translations.Opportunity : Translations.Risk}
                                    </FormField>
                                </Grid>
                                <Grid item sm={3}>
                                    <FormField title={Translations.RiskCategory}>
                                        {state.riskCategory?.title}
                                    </FormField>
                                </Grid>
                                <Grid item sm={3}>
                                    <FormField title={Translations.RiskOwner}>
                                        {state.risk.owner}
                                    </FormField>
                                </Grid>
                                {isRisk &&
                                    <Grid item sm={3}>
                                        <FormField title={Translations.RiskAssesmentFrequency}>
                                            {EnumHelper.RiskAssesmentFrequencyToStr(state.risk.assesmentFrequency)}
                                        </FormField>
                                    </Grid>
                                }
                            </GridRow>
                            <GridRow>
                                {state.riskStandards.length > 0 &&
                                    <Grid item sm={3}>
                                        <FormField title={Translations.RiskStandard}>
                                            {state.riskStandards.map(s => s.title).join(", ")}
                                        </FormField>
                                    </Grid>
                                }
                                {state.riskStakeholders.length > 0 &&
                                    <Grid item sm={3}>
                                        <FormField title={Translations.RiskStakeholder}>
                                            {state.riskStakeholders.map(s => s.title).join(", ")}
                                        </FormField>
                                    </Grid>
                                }
                                {state.riskProcesses.length > 0 &&
                                    <Grid item sm={3}>
                                        <FormField title={state.riskProcessName}>
                                            {state.riskProcesses.map(p => p.title).join(", ")}
                                        </FormField>
                                    </Grid>
                                }
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.Title}>
                                        {state.risk.title}
                                    </FormField>
                                </Grid>
                            </GridRow>
                            {(isRisk
                                ? <>
                                    <GridRow>
                                        <Grid item sm={12}>
                                            <FormField title={Translations.Description}>
                                                {state.risk.description}
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                    <GridRow>
                                        <Grid item sm={12}>
                                            <FormField title={Translations.RiskThreat}>
                                                {state.risk.threat}
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                    <GridRow>
                                        <Grid item sm={12}>
                                            <FormField title={Translations.RiskControls}>
                                                {state.risk.controls}
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                </>
                                : <>
                                    <GridRow>
                                        <Grid item sm={12}>
                                            <FormField title={Translations.OpportunityBenefit}>
                                                {state.risk.opportunityBenefit}
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                    <GridRow>
                                        <Grid item sm={12}>
                                            <FormField title={Translations.OpportunityDescription}>
                                                {state.risk.opportunityDescription}
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                </>
                            )}
                        </>
                    </ListDetailsPanelAccordion>
                    {isRisk && state.risk.opportunity &&
                        <ListDetailsPanelAccordion
                            title={Translations.Opportunity}
                        >
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.OpportunityBenefit} margin="0">
                                        {state.risk.opportunityBenefit}
                                    </FormField>
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.OpportunityDescription}>
                                        {state.risk.opportunityDescription}
                                    </FormField>
                                </Grid>
                            </GridRow>
                        </ListDetailsPanelAccordion>
                    }
                    {isRisk &&
                        <ListDetailsPanelAccordion
                            isLoading={isLoadingAssessments}
                            title={Translations.Assessments}
                        >
                            <RiskEditDialogAssessmentList
                                sessionState={props.sessionState}
                                companyId={props.companyId}
                                riskId={props.riskId}
                                dataId={props.dataId}
                                unitId={state.risk.unitId}
                                assesmentFrequency={state.risk.assesmentFrequency}
                                code={state.risk.code}
                                title={state.risk.title}
                                description={state.risk.description}
                                threat={state.risk.threat}
                                controls={state.risk.controls}
                                isReadOnly={isReadOnly}
                                isNew={false}
                                detailsViewMode={true}
                                onIsLoadingChange={setIsLoadingAssessments}
                                onAssessmentSaved={props.onRefreshList}
                            />
                        </ListDetailsPanelAccordion>
                    }
                    <ListDetailsPanelAccordion
                        isLoading={isLoadingAttachments}
                        title={Translations.Attachments}
                    >
                        <AttachmentListOfRecord
                            companyId={props.companyId}
                            recordId={props.riskId}
                            dataId={props.dataId}
                            title={null}
                            margin="0"
                            onGetAttachments={riskService.getRiskAttachments}
                            onRemoveAttachments={riskService.riskRemoveAttachments}
                            onAddAttachments={riskService.riskAddAttachments}
                            isReadOnly={true}
                            isNewMessage={null}
                            onIsLoadingChange={setIsLoadingAttachments}
                        />
                    </ListDetailsPanelAccordion>
                    <Box sx={{ height: "16px" }}></Box>
                </>
            }
        </ListDetailsPanel>
    );
};
